<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { useAppStore } from '@/core/stores/useAppStore'
import { usePermissions } from '@/core/composables/usePermissions'
import { PERMISSIONS } from '@/core/permissions/constants'
import { notificationsApi, type NotificationData } from '@/modules/notifications/api'
import { handleApiError } from '@/core/api/baseApi'
import { useNotificationSlideOver } from '@/modules/notifications/composables/useNotificationSlideOver'
import NotificationForm from '@/modules/notifications/components/NotificationForm.vue'
import AppSlideOver from '@/components/common/AppSlideOver.vue'
import AppButton from '@/components/common/AppButton.vue'
import { IconSend } from '@tabler/icons-vue'

const { t } = useI18n()
const projectsStore = useProjectsStore()
const appStore = useAppStore()
const { canManage } = usePermissions()
const { isOpen, close } = useNotificationSlideOver()

const loading = ref(false)
const error = ref<string | undefined>(undefined)
const notificationFormRef = ref<{ submitForm: () => void } | null>(null)

const canSendNotifications = canManage(PERMISSIONS.ESHOP.MANAGE)

const triggerSubmit = () => {
  notificationFormRef.value?.submitForm()
}

const handleSubmit = async (notificationData: NotificationData) => {
  if (!projectsStore.currentProject) {
    error.value = t('notifications.messages.noProject')
    return
  }

  loading.value = true
  error.value = undefined

  try {
    await notificationsApi.sendNotification(notificationData, projectsStore.currentProject.code)
    appStore.addNotification(t('notifications.messages.sent'), 'success')
    close()
  } catch (err: unknown) {
    error.value = handleApiError(err)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <AppSlideOver v-model="isOpen" :title="t('notifications.send.title')">
    <div v-if="!projectsStore.currentProject" class="text-center text-gray-500 py-8">
      {{ t('notifications.messages.selectProjectFirst') }}
    </div>
    <div v-else-if="!canSendNotifications" class="text-center text-gray-500 py-8">
      {{ t('notifications.messages.noPermission') }}
    </div>
    <NotificationForm
      v-else
      ref="notificationFormRef"
      :loading="loading"
      :error="error"
      @submit="handleSubmit"
      @cancel="close"
    />

    <template #footer>
      <div class="flex gap-2">
        <AppButton
          variant="primary"
          size="sm"
          :loading="loading"
          :disabled="!projectsStore.currentProject || !canSendNotifications"
          @click="triggerSubmit"
        >
          <template #icon><IconSend class="w-4 h-4" /></template>
          {{ t('notifications.send.button') }}
        </AppButton>
      </div>
    </template>
  </AppSlideOver>
</template>
